/* eslint-disable mozilla/balanced-listeners */
/* global ga, rg4js, amplitude */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';
import * as isEqual from 'lodash.isequal';
import 'core-js/core/string';
import 'core-js/es6/map';
import canAutoplay from 'can-autoplay';

import { DebugOverlay, getDebugInfo } from './debug-overlay';
import { LiveStreamViewerCount } from './live-stream-viewer-count';

import 'video.js/dist/alt/video-js-cdn.css';
import '@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css';
import '@silvermine/videojs-airplay/dist/silvermine-videojs-airplay.css';

let nextPlayerId = 0;

const percentages = new Map()
	.set(5, '5% Watched')
	.set(25, '25% Watched')
	.set(50, '50% Watched')
	.set(75, '75% Watched')
	.set(95, '95% Watched')
	.set(100, '100% Watched');

export class Video extends Component {
	static propTypes = {
		videoId: PropTypes.string,
		className: PropTypes.string,
		playlistId: PropTypes.string,
		posterUrl: PropTypes.string,
		isLiveStream: PropTypes.bool,
		liveStreamActiveViewerCount: PropTypes.number,
		autoPlay: PropTypes.bool,
		isEmbedded: PropTypes.bool,
		disableFullscreen: PropTypes.bool,
		showBrandingBug: PropTypes.bool,
		brandingBugCampaign: PropTypes.string,
		embeddedPlayerCampaign: PropTypes.string,
		brandButtonImageUri: PropTypes.string,
		startTime: PropTypes.number,
		title: PropTypes.string,
		suggestedVideoEndcap: PropTypes.object,
		ratingInfo: PropTypes.shape({
			canRate: PropTypes.bool.isRequired,
			ratingItemId: PropTypes.any.isRequired,
			ratingItemTitle: PropTypes.string,
			returnUrl: PropTypes.string,
		}),
		sources: PropTypes.arrayOf(
			PropTypes.shape({
				url: PropTypes.string,
				mediaType: PropTypes.string,
			}).isRequired
		).isRequired,
		onVideoSegmentPlayed: PropTypes.func,
		onVideoEnd: PropTypes.func,
		onBack: PropTypes.func,
		onPlayNextVideo: PropTypes.func,
		useGoogleAnalytics: PropTypes.bool,
		useAmplitude: PropTypes.bool,
		windowLocation: PropTypes.string,
		upNext: PropTypes.shape({
			startDelay: PropTypes.number,
			parentItemId: PropTypes.string,
			nextItemId: PropTypes.string,
			nextCover: PropTypes.string,
		}),
		registerApiCallback: PropTypes.func,
		EndCapComponent: PropTypes.any,
		UpNextComponent: PropTypes.any,
		RatingOverlayComponent: PropTypes.any,
		ShareOverlayComponent: PropTypes.any,
		remountPlayer: PropTypes.func,
		onError: PropTypes.func,
	};

	state = {
		showDebugInfo: false,
	};

	container = null;
	playerElement = null;

	componentWillMount() {
		this.playerId = `video_${nextPlayerId++}`;
	}

	componentDidMount() {
		this.initializePlayer(this.props);
		document.body.addEventListener('keypress', this.handleKeyPress);
	}

	componentWillReceiveProps(nextProps) {
		const prevProps = this.props;
		if (!nextProps.sources || typeof window === 'undefined') {
			return;
		}

		const player = this.playerId ? window.videojs(this.playerId) : null;

		if (this.props.startTime !== nextProps.startTime) {
			if (player) {
				this.initializePlayer(nextProps);
			}
		}

		if (!isEqual(nextProps.sources, prevProps.sources) || nextProps.videoId !== prevProps.videoId) {
			if (player && player.tech_) {
				const sources = nextProps.sources
					.filter(source => source.url)
					.map(source => {
						const s = {
							src: encodeURI(source.url),
							type: source.mediaType,
						};
						return s;
					});
				if (sources && sources.length) {
					const rate = player.playbackRate();
					player.src(sources);
					this.setState({ ended: false });
					this.initializePlayer(nextProps);
					if (!nextProps.isLiveStream) {
						player.playbackRate(rate);
					}
				}
			}
		}
	}

	componentWillUnmount() {
		this.disposePlayer();

		if (this.props.remountPlayer) {
			this.props.remountPlayer(false);
		}

		clearInterval(this.countdownInterval);

		document.body.removeEventListener('keypress', this.handleKeyPress);
	}

	disposePlayer = () => {
		const player = this.playerId ? window.videojs(this.playerId) : null;

		if (player) {
			player.dispose();
			this.playerId = null;
			this.videoPlayer = null;
		}
	};

	doCountdown = () => {
		this.setState(prevState => ({
			countdownSeconds: prevState.countdownSeconds - 1,
		}));
		if (this.state.countdownSeconds <= 0) {
			clearInterval(this.countdownInterval);
			this.playNextVideo();
		}
	};

	initializePlayer = props => {
		if (!props) {
			return;
		}

		this.playId = uuid();
		this.trackedPercentages = [];

		const {
			startTime,
			isLiveStream,
			posterUrl,
			windowLocation,
			upNext,
			showBrandingBug,
			brandingBugCampaign,
			embeddedPlayerCampaign,
			brandButtonImageUri,
			title,
			onBack,
			registerApiCallback,
			disableFullscreen,
			autoPlay,
			onError,
		} = props;

		const { videojs } = require('./videojs-shim.js'); // eslint-disable-line
		const component = this;
		const hasNextVideo = !!upNext;

		if (this.playerElement) {
			this.latestTrackedTime = startTime;
			const playbackRates = isLiveStream ? null : [0.5, 1, 1.5, 2];
			let update = false;

			if (!this.videoPlayer) {
				this.videoPlayer = videojs(this.playerElement, {
					playerId: this.playerId,
					controlBar: {
						volumePanel: { inline: false },
						fullscreenToggle: !disableFullscreen,
					},
					playbackRates,
					techOrder: ['chromecast', 'html5'],
				});
			} else {
				update = true;
			}

			this.videoPlayer.on('error', function() {
				const player = this;
				let error = player.error();
				if (!isError(error)) {
					try {
						throw new Error(error);
					} catch (err) {
						error = err;
					}
				}
				component.logError(error, { time: component.latestTrackedTime, ...getDebugInfo(player) });

				if (onError) {
					onError(error);
				}
			});

			this.videoPlayer.ready(function initializePlayer() {
				const player = this;
				component.player = player;

				component.trackVideoPlay();
				let timeUpdatesSinceTracking = 0;

				if (!update) {
					// initialize hotkeys
					player.hotkeys({
						enableNumbers: false,
						enableVolumeScroll: false,
					});

					// initialize persistvolume
					try {
						if (player.persistvolume) {
							player.persistvolume({
								namespace: 'faithlifetv',
							});
						}
					} catch (err) {
						component.logError(err, { ...getDebugInfo(player) });
					}

					// initialize chromecast
					player.chromecast();
					player.airPlay();

					player.on('playing', () => {
						component.segmentStartTime = player.currentTime();
					});

					player.on('waiting', () => {
						if (player.tech({ IWillNotUseThisInPlugins: true }).name() === 'Html5') {
							const readyState = player.readyState();
							const isSeeking = player.seeking();

							if (!isSeeking && readyState === 2 /* HAVE_CURRENT_DATA */) {
								component.trackVideoWaiting(getDebugInfo(player));
							}
						}
					});

					// during playback this is fired every 15-250 milliseconds
					player.on('timeupdate', () => {
						timeUpdatesSinceTracking++;
						if (timeUpdatesSinceTracking > 20) {
							component.trackProgress(player);
							timeUpdatesSinceTracking = 0;
						}
					});

					if (isLiveStream) {
						player.tech({ IWillNotUseThisInPlugins: true }).on('usage', event => {
							if (event.name === 'hls-rendition-blacklisted') {
								player.addClass('vjs-waiting');
							}
						});

						player.tech({ IWillNotUseThisInPlugins: true }).on('retryplaylist', () => {
							component.handleVideoEnded();
						});
					}

					player.on('ended', () => {
						component.handleVideoEnded();
					});

					player.on('contentended', () => {
						component.trackProgress();
					});
				}

				player.on('loadedmetadata', () => {
					if (startTime) {
						player.currentTime(startTime);
					} else {
						player.currentTime(0);
					}
				});

				if (autoPlay) {
					canAutoplay.video().then(({ result, error }) => {
						if (result) {
							// check for tech to avoid a video.js bug when the source fails to load
							if (player.tech_) {
								player.play();
							}
						} else {
							console.warn('Autoplay video failed', error);
						}
					});
				}

				if (posterUrl) {
					player.poster(posterUrl);
				}

				const VjsButton = videojs.getComponent('Button');
				const VjsComponent = videojs.getComponent('Component');
				const FullscreenToggle = videojs.getComponent('FullscreenToggle');
				const PlaybackRateMenuButton = videojs.getComponent('PlaybackRateMenuButton');

				class BrandButton extends VjsButton {}
				const createBrandButton = () => {
					const brandLink = `${windowLocation}${embeddedPlayerCampaign}`;
					const buttonProps = {
						className: 'vjs-brand-button vjs-menu-button vjs-button',
						innerHTML: `<a href="${encodeURI(
							brandLink
						)}" target="_top"><img src="${brandButtonImageUri}" style="width: 100%; padding: 2px;" /></a>`,
						tabIndex: 0,
					};
					const attr = {
						role: 'button',
					};
					return videojs.dom.createEl('div', buttonProps, attr);
				};

				class VideoTitle extends VjsComponent {}
				const createTitle = () => {
					const titleProps = {
						className: 'vjs-video-title vjs-menu-button vjs-button',
						innerHTML: `<div><span>${title.escapeHTML()}</span></div>`,
					};
					return videojs.dom.createEl('div', titleProps, {});
				};

				class NextButton extends VjsButton {
					constructor(vjsPlayer, options) {
						super(vjsPlayer, options);
						this.on('click', () => {
							component.playNextVideo();
						});
					}
				}
				const createNextButton = () => {
					const buttonProps = {
						className: 'vjs-next-episode-button vjs-menu-button vjs-button',
						innerHTML: '<div><img src="/public/images/nextepisode-button@2x.png" width="90%"/></div>',
					};
					const attr = {
						role: 'button',
					};
					return videojs.dom.createEl('div', buttonProps, attr);
				};

				class BackButton extends VjsComponent {
					constructor(vjsPlayer, options) {
						super(vjsPlayer, options);
						this.on('click', () => {
							if (onBack) {
								onBack();
							}
						});
					}
				}
				const createBackButton = () => {
					const buttonProps = {
						className: 'vjs-back-button vjs-button',
						innerHTML: '<img src="/public/images/backbutton@2x.png" />',
					};
					return videojs.dom.createEl('div', buttonProps, {});
				};

				class ShareButton extends VjsComponent {
					constructor(vjsPlayer, options) {
						super(vjsPlayer, options);
						this.on('click', () => {
							if (vjsPlayer.isFullscreen()) {
								vjsPlayer.exitFullscreen();
							}
							component.showShareOverlay();
						});
					}
				}
				const createShareButton = () => {
					const buttonProps = {
						className: 'vjs-share-button vjs-button',
						innerHTML: '<img src="/public/images/share.svg" />',
					};
					return videojs.dom.createEl('div', buttonProps, {});
				};

				class BrandingBug extends VjsComponent {
					constructor(vjsPlayer, options) {
						super(vjsPlayer, options);
						this.on('click', e => {
							if (e.target.className === 'close-button') {
								this.el_.style.display = 'none';
							} else {
								window.open(`https://faithlifetv.com/${options.campaign}`, '_top');
							}
						});
					}
				}
				const createBrandingBug = () => {
					const bugProps = {
						className: 'vjs-branding-bug',
						innerHTML:
							'<div class="close-button">&#x2A09;</div><div class="description">Watch the Bible Come Alive</div><img class="logo" src="/public/images/logo@2x.png"/><div class="cta">Explore the library</div>',
					};
					return videojs.dom.createEl('div', bugProps, {});
				};

				const existingNextButtons = document.getElementsByClassName(
					'vjs-next-episode-button vjs-menu-button vjs-button'
				);
				const hasNextButton = existingNextButtons && existingNextButtons.length;
				if (hasNextVideo && !hasNextButton) {
					const nextOptions = {
						el: createNextButton(),
						name: 'nextButton',
					};
					const lastControlBarChild =
						this.controlBar.fullscreenToggle || this.controlBar.children()[this.controlBar.children().length - 1];
					this.controlBar
						.el()
						.insertBefore(new NextButton(this, nextOptions).el(), lastControlBarChild.el().previousSibling);
				} else if (!hasNextVideo && hasNextButton) {
					this.controlBar.el().removeChild(existingNextButtons[0]);
				}

				const existingTitleElements = document.getElementsByClassName('vjs-video-title vjs-menu-button vjs-button');
				const hasTitleElement = existingTitleElements && existingTitleElements.length;
				if (title && !hasTitleElement) {
					const titleOptions = {
						el: createTitle(),
						name: 'titleText',
					};
					this.controlBar
						.el()
						.insertBefore(
							new VideoTitle(this, titleOptions).el(),
							this.controlBar.customControlSpacer.el().nextSibling
						);
				} else if (title && hasTitleElement) {
					// TODO: Fix this the next time the file is edited.
					// eslint-disable-next-line no-unsanitized/property
					existingTitleElements[0].innerHTML = `<div><span>${title.escapeHTML()}</span></div>`;
				}

				const existingRatesElements = document.getElementsByClassName(
					'vjs-playback-rate vjs-menu-button vjs-menu-button-popup vjs-control vjs-button'
				);
				const hasRateElement = existingRatesElements && existingRatesElements.length;
				if (playbackRates && !hasRateElement) {
					const rButton = new PlaybackRateMenuButton(this, { playbackRates: [0.5, 1, 1.5, 2] });
					this.controlBar.addChild(rButton);
					this.controlBar.playbackRateMenuButton = rButton;
				} else if (!playbackRates && hasRateElement) {
					this.controlBar.el().removeChild(existingRatesElements[0]);
				}

				const paintCurrentFrameOnCanvas = canvas => {
					if (!canvas || canvas.tagName !== 'CANVAS' || !player || !player.tech_) {
						return false;
					}

					const imgContext = canvas.getContext('2d');
					const width = (canvas.width = player.videoWidth()); // eslint-disable-line no-param-reassign
					const height = (canvas.height = player.videoHeight()); // eslint-disable-line no-param-reassign

					imgContext.drawImage(player.tech_.el(), 0, 0, width, height);

					return true;
				};

				if (registerApiCallback) {
					registerApiCallback({
						paintCurrentFrameOnCanvas,
						getCurrentTime: () => (player.tech_ ? player.currentTime() : 0),
						getDuration: () => (player.tech_ ? player.duration() : 0),
					});
				}

				const existingBrandButtons = document.getElementsByClassName('vjs-brand-button vjs-menu-button vjs-button');
				const hasBrandButton = existingBrandButtons && existingBrandButtons.length;
				if (windowLocation && !hasBrandButton) {
					const brandOptions = {
						el: createBrandButton(),
						name: 'brandButton',
					};
					const lastControlBarChild =
						this.controlBar.fullscreenToggle || this.controlBar.children()[this.controlBar.children().length - 1];
					this.controlBar
						.el()
						.insertBefore(new BrandButton(this, brandOptions).el(), lastControlBarChild.el().nextSibling);
					const shareOptions = {
						el: createShareButton(),
						name: 'shareButton',
					};

					const shareButton = new ShareButton(this, shareOptions);
					player.addChild(shareButton);

					player.on('useractive', () => {
						shareButton.removeClass('vjs-share-button-hide');
					});

					player.on('userinactive', () => {
						shareButton.addClass('vjs-share-button-hide');
					});
				} else if (!windowLocation && hasBrandButton) {
					const shareButtons = document.getElementsByClassName('vjs-share-button vjs-button vjs-share-button-hide');
					if (shareButtons && shareButtons.length) {
						player.el().removeChild(shareButtons[0]);
					}
					this.controlBar.el().removeChild(existingBrandButtons[0]);
				}

				const existingBrandingBugs = document.getElementsByClassName('vjs-branding-bug');
				const hasBrandingBug = existingBrandingBugs && existingBrandingBugs.length;
				if (showBrandingBug && !hasBrandingBug) {
					const brandingBugOptions = {
						el: createBrandingBug(),
						name: 'brandingBug',
						campaign: brandingBugCampaign,
					};
					const brandingBug = new BrandingBug(this, brandingBugOptions);
					player.addChild(brandingBug);

					// move the bug below the control bar
					const bugEl = brandingBug.el();
					const controlBarEl = this.controlBar.el();
					controlBarEl.parentNode.insertBefore(bugEl.parentNode.removeChild(bugEl), controlBarEl);
				} else if (!showBrandingBug && hasBrandingBug) {
					player.el().removeChild(existingBrandingBugs[0]);
				}

				const existingBackButtons = document.getElementsByClassName('vjs-back-button vjs-button');
				const hasBackButton = existingNextButtons && existingBackButtons.length;
				if (!windowLocation && onBack && !hasBackButton) {
					const backOptions = {
						el: createBackButton(),
						name: 'backButton',
					};
					const backButton = new BackButton(this, backOptions);
					player.addChild(backButton);

					player.on('useractive', () => {
						backButton.removeClass('vjs-back-button-hide');
					});

					player.on('userinactive', () => {
						backButton.addClass('vjs-back-button-hide');
					});
				} else if (!onBack && hasBackButton) {
					player.el().removeChild(existingBackButtons[0]);
				}

				if (!disableFullscreen && !this.controlBar.fullscreenToggle) {
					const fsToggle = new FullscreenToggle(this);
					this.controlBar.addChild(fsToggle);
					this.controlBar.fullscreenToggle = fsToggle;
				} else if (disableFullscreen && this.controlBar.fullscreenToggle) {
					this.controlBar.removeChild(this.controlBar.fullscreenToggle);
				}

				if (this.controlBar.fullscreenToggle) {
					this.controlBar.fullscreenToggle.el().style.marginLeft = 'auto';
				} else {
					const nextButtons = document.getElementsByClassName('vjs-next-episode-button vjs-menu-button vjs-button');
					const nextButton = nextButtons && nextButtons.length ? nextButtons[0] : null;
					if (nextButton) {
						nextButton.style.marginRight = 'auto';
					}
				}

				player.on('useractive', () => {
					component.setState({ userActive: true });
				});
				player.on('userinactive', () => {
					component.setState({ userActive: false });
				});
				component.setState({ userActive: player.userActive() });

				component.updateDebugState();
			});
		}
	};

	updateDebugState = () => {
		if (this.playerId) {
			this.setState({ debugInfo: getDebugInfo(this.player) });
			setTimeout(this.updateDebugState, 1000);
		}
	};

	playNextVideo = () => {
		const { onPlayNextVideo, upNext, playlistId } = this.props;

		if (onPlayNextVideo && upNext && upNext.nextItemId) {
			onPlayNextVideo(upNext.nextItemId, playlistId);
		}
	};

	logError = (error, data) => {
		console.warn(error);
		if (typeof rg4js === 'function') {
			rg4js('send', {
				error,
				customData: [
					{
						mediaId: this.props.videoId,
						...data,
					},
				],
			});
		}
	};

	setPlayerRef = ref => {
		this.playerElement = ref;
	};

	trackVideoPlay = () => {
		const { useGoogleAnalytics, useAmplitude, videoId, title } = this.props;
		if (useGoogleAnalytics && typeof ga !== 'undefined' && typeof ga === 'function') {
			ga('send', {
				hitType: 'event',
				eventCategory: 'Video',
				eventAction: 'Played',
				eventLabel: `${videoId}`,
				nonInteraction: true,
			});
		}
		if (useAmplitude && typeof amplitude !== 'undefined' && typeof amplitude.getInstance === 'function') {
			amplitude.getInstance().logEvent('Video - Played', {
				'Item Id': videoId,
				Title: title || 'Not Set',
			});
		}
	};

	trackProgress = () => {
		const { onVideoSegmentPlayed, useGoogleAnalytics, useAmplitude, isLiveStream, videoId, title } = this.props;

		this.latestTrackedTime = this.player.currentTime();

		const startTime =
			this.segmentStartTime > this.latestTrackedTime ? Math.round(this.latestTrackedTime) : this.segmentStartTime;
		const currentTime = Math.round(this.latestTrackedTime);

		if (!isLiveStream) {
			const trackedPercentages = this.trackedPercentages;
			const progress = Math.round((currentTime / this.player.duration()) * 100);
			for (const percent of percentages.keys()) {
				if (progress >= percent && !trackedPercentages.includes(percent)) {
					if (useGoogleAnalytics && typeof ga !== 'undefined' && typeof ga === 'function') {
						ga('send', {
							hitType: 'event',
							eventCategory: 'Video',
							eventAction: `${percentages.get(percent)}`,
							eventLabel: `${videoId}`,
							eventValue: Math.floor(currentTime),
							nonInteraction: true,
						});
					}
					if (useAmplitude && typeof amplitude !== 'undefined' && typeof amplitude.getInstance === 'function') {
						amplitude.getInstance().logEvent('Video - Progress', {
							'Item Id': videoId,
							'Percent Watched': percent,
							Title: title || 'Not Set',
						});
					}
					trackedPercentages.push(percent);
				}
			}
		}

		if (onVideoSegmentPlayed && currentTime > 0 && startTime !== undefined && videoId) {
			const qualityLevels = this.player.qualityLevels();

			let bitrate;
			if (qualityLevels && qualityLevels.selectedIndex !== -1) {
				const selectedQualityLevel = qualityLevels[qualityLevels.selectedIndex];
				bitrate = selectedQualityLevel && selectedQualityLevel.bitrate;
			}
			onVideoSegmentPlayed({
				playId: this.playId,
				endTime: currentTime,
				startTime,
				bitrate,
			});
			this.segmentStartTime = currentTime;
		}
	};

	trackVideoWaiting = data => {
		const { useAmplitude, videoId, title } = this.props;
		if (useAmplitude && typeof amplitude !== 'undefined' && typeof amplitude.getInstance === 'function') {
			const eventProps = {
				'Item Id': videoId,
				Title: title || 'Not Set',
			};
			if (data && data.currentSource && data.currentSource.src) {
				eventProps['Video Source'] = data.currentSource.src;
			}
			if (
				data &&
				data.qualityLevels &&
				Array.isArray(data.qualityLevels.levels) &&
				data.qualityLevels.levels.length &&
				Number.isInteger(data.qualityLevels.selectedIndex)
			) {
				const qualityLevel = data.qualityLevels.levels[data.qualityLevels.selectedIndex];
				if (qualityLevel && qualityLevel.width) {
					eventProps['Video Width'] = qualityLevel.width;
				}
				if (qualityLevel && qualityLevel.height) {
					eventProps['Video Height'] = qualityLevel.height;
				}
				if (qualityLevel && qualityLevel.bitrate) {
					eventProps['Video Bitrate'] = qualityLevel.bitrate;
				}
			}
			if (data && data.stats) {
				eventProps['Video Stats'] = data.stats;
			}
			amplitude.getInstance().logEvent('Video - Waiting', eventProps);
		}
	};

	hideDebugOverlay = () => {
		this.setState({
			showDebugInfo: false,
		});
	};

	showShareOverlay = () => {
		this.setState({
			showShareOverlay: true,
		});
	};

	hideShareOverlay = () => {
		this.setState({
			showShareOverlay: false,
		});
	};

	hideRatingOverlay = () => {
		this.setState({
			showRatingOverlay: false,
		});
	};

	handleKeyPress = e => {
		if (e.code === 'KeyD' && e.ctrlKey && e.altKey && e.shiftKey) {
			this.setState(prevState => ({ showDebugInfo: !prevState.showDebugInfo }));
		}
	};

	handleVideoEnded = () => {
		if (this.state.ended) {
			return;
		}

		this.setState({ ended: true, showRatingOverlay: true });

		const { upNext, onVideoEnd } = this.props;

		if (upNext) {
			if (upNext.startDelay) {
				this.setState({ countdownSeconds: upNext.startDelay });
				this.countdownInterval = setInterval(this.doCountdown, 1000);
			} else {
				this.playNextVideo();
			}
		} else {
			const player = this.playerId ? window.videojs(this.playerId) : null;
			if (player && player.isFullscreen()) {
				player.exitFullscreen();
			}
		}

		if (onVideoEnd) {
			onVideoEnd();
		}
	};

	render() {
		const {
			videoId,
			sources,
			className,
			upNext,
			suggestedVideoEndcap,
			ratingInfo,
			playlistId,
			isEmbedded,
			EndCapComponent,
			UpNextComponent,
			RatingOverlayComponent,
			ShareOverlayComponent,
			isLiveStream,
			liveStreamActiveViewerCount,
		} = this.props;

		const {
			showDebugInfo,
			debugInfo,
			ended,
			countdownSeconds,
			showShareOverlay,
			showRatingOverlay,
			userActive,
		} = this.state;

		const canDisplayUpNext = !!UpNextComponent && ended && !!upNext && countdownSeconds;
		const canDisplayEndCap = !!EndCapComponent && ended && !!suggestedVideoEndcap && !canDisplayUpNext;
		const canDisplayRating = !!RatingOverlayComponent && ended && ratingInfo.canRate && showRatingOverlay;
		// deliberately using != instead of !== here to handle undefined and null values
		const canDisplayLiveStreamViewerCount = isLiveStream && liveStreamActiveViewerCount != null;

		return (
			<div>
				{!sources || !sources.length ? null : (
					<div>
						<video
							id={this.playerId}
							ref={this.setPlayerRef}
							className={`video-js ${className}`}
							preload="auto"
							controls
							style={{ width: '100%', height: '100%', position: 'absolute', top: 0 }}
						>
							{sources.map(source => (
								<source key={source.url} src={encodeURI(source.url)} type={source.mediaType} />
							))}
						</video>
					</div>
				)}
				{!canDisplayEndCap ? null : (
					<EndCapComponent suggestedVideoEndcap={suggestedVideoEndcap} adParams={{ itemId: videoId }} />
				)}
				{!canDisplayUpNext ? null : (
					<UpNextComponent
						cover={upNext.nextCover}
						countdown={countdownSeconds}
						nextVideoId={upNext.nextItemId}
						browseId={upNext.parentItemId}
						playlistId={playlistId}
						playerId={this.playerId}
						title={upNext.title}
					/>
				)}
				{!canDisplayRating ? null : (
					<RatingOverlayComponent
						ratingItemId={ratingInfo.ratingItemId}
						ratingItemTitle={ratingInfo.ratingItemTitle}
						returnUrl={ratingInfo.returnUrl}
						isEmbedded={isEmbedded}
						hideRating={this.hideRatingOverlay}
					/>
				)}
				{!showDebugInfo ? null : <DebugOverlay debugInfo={debugInfo} onDismiss={this.hideDebugOverlay} />}
				{!canDisplayLiveStreamViewerCount ? null : (
					<LiveStreamViewerCount userActive={userActive} liveStreamActiveViewerCount={liveStreamActiveViewerCount} />
				)}
				{videoId
					? ShareOverlayComponent && (
							<ShareOverlayComponent active={showShareOverlay} hideShare={this.hideShareOverlay} itemId={videoId} />
					  )
					: false}
			</div>
		);
	}
}

function isError(err) {
	return typeof err === 'object' && err.message !== undefined;
}
